import type {
  EquationAST,
  InterestCalculationDetails,
  ValueWithContext,
} from 'types/title-calculations/types';
import type { HighlightRunsheetRowProps } from 'lib/types';
import { TOP_RUNSHEET_SECTIONS_HEIGHT_IN_PX } from 'lib/constants';
import { EntityLink } from 'components/entity-link';
import { formatInterest } from 'utils/format-interest';
import { occurrenceKeys } from 'utils/occurrence-keys';
import type {
  OnSelectedTransaction,
  ValueContext,
} from './calculation-details';
import { Operation, type OperationContextProps, Value } from './operation';
import style from './interest-math.module.scss';

/**
 * A share's value, with the equation that produced it when there is one.
 * @param {object} props The value.
 * @param {EquationAST | ValueWithContext} props.value The value or equation.
 * @return {JSX.Element} The value.
 */
function EquationOrValue({
  value,
  onJump,
  onSee,
}: {
  value: EquationAST | ValueWithContext;
} & OperationContextProps) {
  if (!('operation' in value)) {
    return <Value value={value} onSee={onSee} onJump={onJump} />;
  }

  return (
    <>
      {formatInterest(value.value)} ={' '}
      <Operation equationAST={value} onJump={onJump} onSee={onSee} />
    </>
  );
}

type InterestMathProps = {
  interest: InterestCalculationDetails | null;
  setSelectedTransaction: OnSelectedTransaction;
  interpretationId: number;
  changed: boolean;
} & HighlightRunsheetRowProps;

/**
 * An interest's math, share by share for each entity it burdens.
 * @param {InterestMathProps} props The interest.
 * @return {JSX.Element | null} The breakdown, or nothing without an interest.
 */
function InterestMath({
  interest,
  setSelectedTransaction,
  refsMap,
  setHighlightedRow,
  interpretationId,
  changed,
}: InterestMathProps) {
  if (!interest) return null;

  const shares = [...interest.byBurdenedEntity].sort((a, b) =>
    (a.entity?.name ?? '') < (b.entity?.name ?? '') ? -1 : 1
  );
  const shareKeys = occurrenceKeys(
    shares.map((share) => String(share.entity?.id ?? ''))
  );

  /**
   * Opens the transaction a value came from.
   * @param {ValueContext} context The value's transaction.
   */
  const onSee = (context: ValueContext) => {
    setSelectedTransaction(context);
  };

  /**
   * Scrolls to and briefly highlights the runsheet row a value came from.
   * @param {number} jumpToId The row's interpretation id.
   */
  const onJump = (jumpToId: number) => {
    const ref = refsMap.get(jumpToId);
    if (!ref) return;

    const topPosition = ref.getBoundingClientRect().top;
    window.scrollTo({
      top: topPosition + window.screenY - TOP_RUNSHEET_SECTIONS_HEIGHT_IN_PX,
      behavior: 'smooth',
    });
    setHighlightedRow({ current: jumpToId, previous: interpretationId });
    ref.classList.add(style.highlight);
    setTimeout(() => {
      ref.classList.remove(style.highlight);
    }, 2000);
  };

  return (
    <table className={style.interestTable}>
      <thead>
        <tr>
          <th>Burdened Entity</th>
          {changed && <th>Before</th>}
          {changed && <th>After</th>}
          {changed || <th>Value</th>}
        </tr>
      </thead>
      <tbody>
        {shares.map((share, i) => (
          <tr key={shareKeys[i]}>
            <td>
              <p className={style.truncateText}>
                <EntityLink id={share.entity?.id} name={share.entity?.name} />
              </p>
            </td>
            <td>
              <div className={style.alignOperationMiddle}>
                <EquationOrValue
                  value={share.before}
                  onSee={onSee}
                  onJump={onJump}
                />
              </div>
            </td>
            {changed && (
              <td>
                <div className={style.alignOperationMiddle}>
                  <EquationOrValue
                    value={share.after}
                    onSee={onSee}
                    onJump={onJump}
                  />
                </div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export { InterestMath };
